/**
 * Bulk upload: inspect → map → preview → commit, with nothing silent.
 *
 * The parse is shown per file before a byte is committed, corrections are made here rather
 * than guessed by the server, new documents require an explicit confirmation, and the
 * commit reports per file — the ones that landed and the ones that did not, with reasons.
 */
import { useCallback, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileUp, FolderOpen, Loader2, Play, Upload } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';

export default function EdmBulkUpload({ projectId }) {
  const T = useNfaTheme();
  const [patterns, setPatterns] = useState(null);
  const [regex, setRegex] = useState('');
  const [batch, setBatch] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(null);
  const [staged, setStaged] = useState(0);
  const [commit, setCommit] = useState(null);
  const [allowNew, setAllowNew] = useState(false);
  const [err, setErr] = useState(null);
  const pick = useRef(null);
  const pickFolder = useRef(null);
  const pickZip = useRef(null);

  const loadPatterns = useCallback(async () => {
    const r = await api.get(`/edm/bulk/patterns?project_id=${projectId}`);
    setPatterns(r.data);
    setRegex((r.data.default || r.data.library[0]).regex);
  }, [projectId]);

  const inspect = async (list) => {
    setBusy('inspect'); setErr(null); setCommit(null); setStaged(0);
    try {
      const r = await api.post(`/edm/bulk/batches?project_id=${projectId}`, {
        files: list.map((f) => ({ filename: f.name, size: f.size })),
        regex: regex || undefined,
      });
      setBatch(r.data); setFiles(list);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'The batch could not be inspected');
    } finally { setBusy(null); }
  };

  const correct = async (index, field, value) => {
    const r = await api.put(`/edm/bulk/batches/${batch.batch_id}/mapping`, {
      corrections: [{ index, [field]: value }],
    });
    setBatch(r.data);
  };

  const confirmNew = async (value) => {
    setAllowNew(value);
    const r = await api.put(`/edm/bulk/batches/${batch.batch_id}/mapping`,
      { corrections: [], allowNew: value });
    setBatch(r.data);
  };

  const stageAll = async () => {
    setBusy('stage'); setErr(null);
    let done = 0;
    for (const row of batch.files) {
      const f = files.find((x) => x.name === row.filename);
      if (!f || row.staged) { done += 1; setStaged(done); continue; }
      const fd = new FormData();
      fd.append('file', f, f.name);
      try {
        await api.post(`/edm/bulk/batches/${batch.batch_id}/stage/${row.index}`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch { /* a file that fails to upload is reported at commit, by name */ }
      done += 1; setStaged(done);
    }
    const r = await api.get(`/edm/bulk/batches/${batch.batch_id}`);
    setBatch(r.data); setBusy(null);
  };

  const zip = async (file) => {
    setBusy('zip'); setErr(null);
    try {
      let b = batch;
      if (!b) {
        const created = await api.post(`/edm/bulk/batches?project_id=${projectId}`,
          { files: [{ filename: file.name, size: file.size }], regex: regex || undefined });
        b = created.data;
        await api.put(`/edm/bulk/batches/${b.batch_id}/mapping`,
          { corrections: [{ index: 0, action: 'skip' }] });
      }
      const fd = new FormData();
      fd.append('file', file, file.name);
      await api.post(`/edm/bulk/batches/${b.batch_id}/stage-zip`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await api.get(`/edm/bulk/batches/${b.batch_id}`);
      setBatch(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'The archive could not be read');
    } finally { setBusy(null); }
  };

  const doCommit = async () => {
    setBusy('commit'); setErr(null);
    try {
      const r = await api.post(`/edm/bulk/batches/${batch.batch_id}/commit`);
      setCommit(r.data);
      const b = await api.get(`/edm/bulk/batches/${batch.batch_id}`);
      setBatch(b.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'The commit was refused');
    } finally { setBusy(null); }
  };

  const savePattern = async () => {
    await api.post(`/edm/bulk/patterns?project_id=${projectId}`,
      { regex, label: 'Saved from the bulk upload screen' });
    await loadPatterns();
  };

  if (!patterns) {
    return (
      <div className="rounded-lg p-6 text-[12px]" data-testid="edm-bulk-upload-intro"
        style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <p style={{ color: T.body }}>
          Upload hundreds of files at once: the filename parse is shown per file before
          anything commits, new documents need an explicit confirmation, and the commit is
          resumable and reports per file.
        </p>
        <button onClick={loadPatterns} data-testid="edm-bulk-start"
          className="mt-3 px-3 py-1.5 rounded text-[11.5px] font-medium"
          style={{ background: T.primary, color: '#fff' }}>Start a batch</button>
      </div>
    );
  }

  const c = batch?.counts;
  return (
    <div className="space-y-3" data-testid="edm-bulk-upload">
      <div className="rounded-lg p-3 space-y-2"
        style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: T.muted }}>
          1 · Filename pattern for this project
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="edm-bulk-pattern-select" onChange={(e) => setRegex(e.target.value)}
            value={regex} className="px-2 py-1.5 rounded text-[11px] max-w-[420px]"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }}>
            {(patterns.saved || []).map((p) => (
              <option key={p.regex} value={p.regex}>saved · {p.label}</option>))}
            {patterns.library.map((p) => <option key={p.key} value={p.regex}>{p.label}</option>)}
          </select>
          <input value={regex} onChange={(e) => setRegex(e.target.value)}
            data-testid="edm-bulk-pattern-regex"
            className="px-2 py-1.5 rounded text-[10.5px] font-mono flex-1 min-w-[260px]"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <button onClick={savePattern} data-testid="edm-bulk-pattern-save"
            className="px-2.5 py-1.5 rounded text-[11px]"
            style={{ border: `1px solid ${T.border}`, color: T.body }}>Save for this project</button>
        </div>
        <div className="text-[10.5px]" style={{ color: T.muted }}>{patterns.note}</div>
      </div>

      <div className="rounded-lg p-3 flex items-center gap-2 flex-wrap"
        style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <div className="text-[10.5px] uppercase tracking-[0.16em] w-full"
          style={{ color: T.muted }}>2 · Choose files</div>
        <input ref={pick} type="file" multiple hidden data-testid="edm-bulk-file-input"
          onChange={(e) => inspect([...e.target.files])} />
        <input ref={pickFolder} type="file" multiple hidden webkitdirectory=""
          data-testid="edm-bulk-folder-input" onChange={(e) => inspect([...e.target.files])} />
        <input ref={pickZip} type="file" accept=".zip" hidden data-testid="edm-bulk-zip-input"
          onChange={(e) => e.target.files[0] && zip(e.target.files[0])} />
        <button onClick={() => pick.current.click()} data-testid="edm-bulk-pick-files"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11.5px]"
          style={{ border: `1px solid ${T.border}`, color: T.body }}>
          <FileUp className="w-3.5 h-3.5" /> Files
        </button>
        <button onClick={() => pickFolder.current.click()} data-testid="edm-bulk-pick-folder"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11.5px]"
          style={{ border: `1px solid ${T.border}`, color: T.body }}>
          <FolderOpen className="w-3.5 h-3.5" /> Folder
        </button>
        <button onClick={() => pickZip.current.click()} data-testid="edm-bulk-pick-zip"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11.5px]"
          style={{ border: `1px solid ${T.border}`, color: T.body }}>
          <Upload className="w-3.5 h-3.5" /> Zip archive
        </button>
        {busy === 'inspect' && <Loader2 className="w-4 h-4 animate-spin" />}
        {err && <span className="text-[11px]" style={{ color: '#EF4444' }}>{String(err)}</span>}
      </div>

      {batch && (
        <div className="rounded-lg" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
          <div className="p-3 flex items-center gap-3 flex-wrap"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: T.muted }}>
              3 · Preview
            </div>
            <span className="text-[11.5px]" style={{ color: T.ink }} data-testid="edm-bulk-counts">
              {c.total} file(s) · {c.matched} matched · {c.new} new · {c.unparsed} unparsed ·
              {' '}{c.paired} paired · {c.staged} uploaded · {c.committed} committed
            </span>
            <label className="text-[11px] inline-flex items-center gap-1.5 ml-auto"
              style={{ color: c.new ? T.amber : T.muted }}>
              <input type="checkbox" checked={allowNew} data-testid="edm-bulk-allow-new"
                onChange={(e) => confirmNew(e.target.checked)} />
              create {c.new} new document(s)
            </label>
            <button onClick={stageAll} disabled={busy} data-testid="edm-bulk-upload-all"
              className="px-3 py-1.5 rounded text-[11px]"
              style={{ border: `1px solid ${T.border}`, color: T.body }}>
              {busy === 'stage' ? `Uploading ${staged}/${c.total}…` : 'Upload the bytes'}
            </button>
            <button onClick={doCommit} disabled={busy || !c.staged} data-testid="edm-bulk-commit"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[11.5px] font-medium disabled:opacity-40"
              style={{ background: T.primary, color: '#fff' }}>
              {busy === 'commit' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Play className="w-3.5 h-3.5" />} Commit
            </button>
          </div>

          <div className="max-h-[420px] overflow-auto">
            {batch.files.map((r) => (
              <div key={r.index} data-testid={`edm-bulk-row-${r.index}`}
                className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] items-center"
                style={{ borderBottom: `1px solid ${T.border}` }}>
                <div className="col-span-3 truncate font-mono" style={{ color: T.body }}>
                  {r.filename}
                </div>
                <input value={r.document_no || ''} placeholder="document no."
                  data-testid={`edm-bulk-no-${r.index}`}
                  onChange={(e) => correct(r.index, 'document_no', e.target.value)}
                  className="col-span-2 px-1.5 py-1 rounded text-[10.5px] font-mono"
                  style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
                <input value={r.revision || ''} placeholder="rev"
                  data-testid={`edm-bulk-rev-${r.index}`}
                  onChange={(e) => correct(r.index, 'revision', e.target.value)}
                  className="col-span-1 px-1.5 py-1 rounded text-[10.5px]"
                  style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
                <div className="col-span-1 text-[10.5px]" style={{ color: T.muted }}>{r.role}</div>
                <div className="col-span-2 text-[10.5px] truncate"
                  style={{ color: r.matchedDocumentId ? T.green : T.amber }}>
                  {r.matchedDocumentId ? `→ ${r.matchedTitle || 'matched'}`
                    : r.isNew ? 'NEW document' : 'no match'}
                  {r.targetRevisionCode ? ` · Rev ${r.targetRevisionCode}`
                    : r.revisionWouldBeOpened ? ` · opens Rev ${r.revisionWouldBeOpened}` : ''}
                </div>
                <div className="col-span-3 text-[10.5px]" style={{ color: T.muted }}>
                  {r.outcome && (
                    <span data-testid={`edm-bulk-outcome-${r.index}`}
                      style={{ color: r.outcome === 'LANDED' ? T.green
                        : r.outcome === 'FAILED' ? '#EF4444' : T.amber }}>
                      {r.outcome}{' '}
                    </span>
                  )}
                  {r.pairingNote || r.problem || r.reason || (r.staged ? 'uploaded' : '')}
                </div>
              </div>
            ))}
          </div>

          {commit && (
            <div className="p-3 text-[11.5px] space-y-1" data-testid="edm-bulk-commit-result"
              style={{ borderTop: `1px solid ${T.border}` }}>
              <div className="inline-flex items-center gap-1.5"
                style={{ color: commit.failed ? T.amber : T.green }}>
                {commit.failed ? <AlertTriangle className="w-4 h-4" />
                  : <CheckCircle2 className="w-4 h-4" />}
                {commit.note}
              </div>
              {commit.failures?.length > 0 && (
                <div className="max-h-40 overflow-auto" data-testid="edm-bulk-failures">
                  {commit.failures.map((f) => (
                    <div key={f.index} className="flex gap-2 py-0.5">
                      <span className="font-mono" style={{ color: '#EF4444', minWidth: 220 }}>
                        {f.filename}</span>
                      <span style={{ color: T.muted }}>{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              {commit.translations && (
                <div style={{ color: T.muted }} data-testid="edm-bulk-translations">
                  Translations: {commit.translations.submitted} submitted,
                  {' '}{commit.translations.held} held. {commit.translations.note}
                </div>
              )}
              {commit.resumable && (
                <button onClick={doCommit} data-testid="edm-bulk-resume"
                  className="px-3 py-1.5 rounded text-[11px] font-medium"
                  style={{ background: T.primary, color: '#fff' }}>
                  Resume — {commit.remaining} file(s) left
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
